import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import img from '../images-src/logo.jpeg';

const GeneralReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter states
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchReceiptNo, setSearchReceiptNo] = useState('');
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  const [searchPaymentMode, setSearchPaymentMode] = useState('');
  
  // Receipt modal states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    customer_name: '',
    receipt_number: '',
    reference_no: '',
    amount: '',
    receipt_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Cash'
  });
  const [receiptErrors, setReceiptErrors] = useState({});
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);
  const [receiptSuccess, setReceiptSuccess] = useState('');

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Fetch receipts on component mount
  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/greceipts`);
      const data = Array.isArray(response.data) 
        ? response.data 
        : response.data?.data || [];
      setReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
      setError('Failed to load receipts');
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter receipts based on search criteria
  const filteredReceipts = receipts.filter(receipt => {
    // Customer name match: if no search, true; otherwise check if contains search text
    const customerMatch = !searchCustomer || (receipt.customer_name?.toLowerCase() || '').includes(searchCustomer.toLowerCase());
    
    // Receipt number match: if no search, true; otherwise check if contains search text
    const receiptNoMatch = !searchReceiptNo || (receipt.receipt_number?.toLowerCase() || '').includes(searchReceiptNo.toLowerCase());
    
    // Date from match: if no date specified, true; otherwise check if receipt date is after search date
    const dateFromMatch = !searchDateFrom || (receipt.receipt_date && new Date(receipt.receipt_date) >= new Date(searchDateFrom));
    
    // Date to match: if no date specified, true; otherwise check if receipt date is before search date
    const dateToMatch = !searchDateTo || (receipt.receipt_date && new Date(receipt.receipt_date) <= new Date(searchDateTo + 'T23:59:59'));
    
    // Payment mode match: if no mode selected, true; otherwise check if matches
    const paymentModeMatch = !searchPaymentMode || receipt.payment_mode?.toLowerCase().includes(searchPaymentMode.toLowerCase());

    return customerMatch && receiptNoMatch && dateFromMatch && dateToMatch && paymentModeMatch;
  });

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Open receipt modal
  const openReceiptModal = () => {
    setReceiptForm({
      customer_name: '',
      receipt_number: '',
      reference_no: '',
      amount: '',
      receipt_date: new Date().toISOString().split('T')[0],
      payment_mode: 'Cash'
    });
    setReceiptErrors({});
    setReceiptSuccess('');
    setShowReceiptModal(true);
  };

  // Close receipt modal
  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptForm({
      customer_name: '',
      receipt_number: '',
      reference_no: '',
      amount: '',
      receipt_date: new Date().toISOString().split('T')[0],
      payment_mode: 'Cash'
    });
    setReceiptErrors({});
  };

  // Handle receipt form input change
  const handleReceiptFormChange = (field, value) => {
    setReceiptForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (receiptErrors[field]) {
      setReceiptErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate receipt form
  const validateReceiptForm = () => {
    const newErrors = {};

    if (!receiptForm.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }

    if (!receiptForm.amount) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(receiptForm.amount);
      if (amount <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      }
    }

    if (!receiptForm.receipt_date) {
      newErrors.receipt_date = 'Receipt date is required';
    }

    if (!receiptForm.payment_mode) {
      newErrors.payment_mode = 'Payment mode is required';
    }

    setReceiptErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit receipt
  const submitReceipt = async (e) => {
    e.preventDefault();

    if (!validateReceiptForm()) {
      return;
    }

    setReceiptSubmitting(true);

    try {
      const receiptData = {
        customer_name: receiptForm.customer_name.trim(),
        receipt_number: receiptForm.receipt_number.trim() || null,
        reference_no: receiptForm.reference_no.trim() || null,
        amount: parseFloat(receiptForm.amount),
        receipt_date: receiptForm.receipt_date,
        payment_mode: receiptForm.payment_mode
      };

      const response = await axios.post(`${apiUrl}/api/greceipts`, receiptData);

      setReceiptSuccess('Receipt added successfully! ✅');

      // Refresh receipts
      setTimeout(() => {
        fetchReceipts();
        closeReceiptModal();
      }, 1500);
    } catch (err) {
      console.error('Error submitting receipt:', err);
      setReceiptErrors({
        submit: err.response?.data?.message || 'Failed to add receipt. Please try again.'
      });
    } finally {
      setReceiptSubmitting(false);
    }
  };

  // Delete receipt
  const deleteReceipt = async (id) => {
    if (window.confirm('Are you sure you want to delete this receipt? This action cannot be undone.')) {
      try {
        await axios.delete(`${apiUrl}/api/greceipts/${id}`);
        alert('Receipt deleted successfully! ✅');
        fetchReceipts();
      } catch (err) {
        console.error('Error deleting receipt:', err);
        alert('Failed to delete receipt. Please try again.');
      }
    }
  };

  // Export single receipt to PDF
  const exportSingleReceiptPDF = async (receipt) => {
    try {
      const SingleReceiptPDF = () => (
        <Document>
          <Page size="A4" style={pdfStyles.page}>
            <View style={pdfStyles.header}>
              <View style={pdfStyles.headerLeft}>
                <Image src={img} style={{ width: 90, height: 110, marginBottom: 5, marginLeft: 10 }} />
              </View>
              <View style={pdfStyles.headerRight}>
                <Text style={pdfStyles.receiptTitle}>RECEIPT</Text>
                <Text style={pdfStyles.companyName}>Diamond Quality Construction L.L.C.SP</Text>
                <Text style={pdfStyles.companyDetails}>TRN: 104836477000001</Text>
                <Text style={pdfStyles.companyDetails}>Sharjah - Muweilah - University City Street</Text>
                <Text style={pdfStyles.companyDetails}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
                <Text style={pdfStyles.companyDetails}>0557001522</Text>
                <Text style={pdfStyles.companyDetails}>dqcc01@gmail.com</Text>
                <Text style={pdfStyles.companyDetails}>www.dqcc.ae</Text>
              </View>
            </View>

            <View style={pdfStyles.infoSection}>
              <View style={pdfStyles.infoBlock}>
                <Text style={pdfStyles.infoLabel}>Receipt Number</Text>
                <Text style={pdfStyles.infoValue}>{receipt.receipt_number || 'N/A'}</Text>
              </View>
              <View style={pdfStyles.infoBlock}>
                <Text style={pdfStyles.infoLabel}>Receipt Date</Text>
                <Text style={pdfStyles.infoValue}>{formatDate(receipt.receipt_date)}</Text>
              </View>
              <View style={pdfStyles.infoBlock}>
                <Text style={pdfStyles.infoLabel}>Customer Name</Text>
                <Text style={pdfStyles.infoValue}>{receipt.customer_name || 'N/A'}</Text>
              </View>
            </View>
            
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableRow}>
                <Text style={[pdfStyles.col2, { fontWeight: 'bold' }]}>Reference No:</Text>
                <Text style={pdfStyles.col2}>{receipt.reference_no || '-'}</Text>
              </View>
              <View style={pdfStyles.tableRow}>
                <Text style={[pdfStyles.col2, { fontWeight: 'bold' }]}>Amount:</Text>
                <Text style={pdfStyles.col2}>{formatCurrency(receipt.amount)}</Text>
              </View>
              <View style={pdfStyles.tableRow}>
                <Text style={[pdfStyles.col2, { fontWeight: 'bold' }]}>Payment Mode:</Text>
                <Text style={pdfStyles.col2}>{receipt.payment_mode}</Text>
              </View>
            </View>

            <Text style={pdfStyles.footerText}>
              Generated on {new Date().toLocaleString('en-GB')}
            </Text>
          </Page>
        </Document>
      );

      const pdfDocument = React.createElement(SingleReceiptPDF);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${receipt.receipt_number || receipt.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    try {
      const pdfDocument = React.createElement(GeneralReceiptsPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GeneralReceipts_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    page: {
      padding: 10,
      fontFamily: 'Helvetica',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      borderBottomWidth: 2,
      borderBottomColor: '#0891b2',
      paddingBottom: 20,
    },
    headerLeft: {
      flex: 1,
    },
    headerRight: {
      flex: 1,
    },
    receiptTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#0891b2',
      textAlign: 'right',
      marginBottom: 5,
    },
    companyName: {
      fontSize: 10,
      fontWeight: 'bold',
      color: '#000',
      textAlign: 'right',
      marginBottom: 2,
    },
    companyDetails: {
      fontSize: 8,
      color: '#333',
      textAlign: 'right',
      marginBottom: 1,
    },
    infoSection: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 15,
      backgroundColor: '#ffffff',
      padding: 10,
    },
    infoBlock: {
      flex: 1,
      marginRight: 15,
    },
    infoLabel: {
      fontSize: 8,
      color: '#333',
      marginBottom: 2,
      textTransform: 'uppercase',
      fontWeight: 'bold',
    },
    infoValue: {
      fontSize: 10,
      fontWeight: '600',
      color: '#000',
    },
    title: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 15,
      color: '#0891b2',
    },
    subtitle: {
      fontSize: 10,
      color: '#666',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: 9,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    tableRow: {
      flexDirection: 'row',
      fontSize: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 6,
    },
    col1: {
      width: '15%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col2: {
      width: '20%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col3: {
      width: '15%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col4: {
      width: '15%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col5: {
      width: '15%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col6: {
      width: '12%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    footerText: {
      fontSize: 9,
      color: '#666',
      marginTop: 10,
    },
  });

  // PDF Document Component
  const GeneralReceiptsPDFDocument = () => {
    const hasDateFilter = searchDateFrom || searchDateTo;
    const dateRangeText = hasDateFilter
      ? `${searchDateFrom ? formatDate(searchDateFrom) : 'Start'} - ${searchDateTo ? formatDate(searchDateTo) : 'End'}`
      : '';

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <View style={pdfStyles.header}>
            <View style={pdfStyles.headerLeft}>
              <Image src={img} style={{ width: 90, height: 110, marginBottom: 5, marginLeft: 10 }} />
            </View>
            <View style={pdfStyles.headerRight}>
              <Text style={pdfStyles.receiptTitle}>RECEIPTS REPORT</Text>
              <Text style={pdfStyles.companyName}>Diamond Quality Construction L.L.C.SP</Text>
              <Text style={pdfStyles.companyDetails}>TRN: 104836477000001</Text>
              <Text style={pdfStyles.companyDetails}>Sharjah - Muweilah - University City Street</Text>
              <Text style={pdfStyles.companyDetails}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
              <Text style={pdfStyles.companyDetails}>0557001522</Text>
              <Text style={pdfStyles.companyDetails}>dqcc01@gmail.com</Text>
              <Text style={pdfStyles.companyDetails}>www.dqcc.ae</Text>
            </View>
          </View>
          
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Generated on {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Text>
            {hasDateFilter && (
              <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
                Period: {dateRangeText}
              </Text>
            )}
          </View>

          <View style={pdfStyles.table}>
            {/* Table Header */}
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>Receipt #</Text>
              <Text style={pdfStyles.col2}>Customer</Text>
              <Text style={pdfStyles.col3}>Reference #</Text>
              <Text style={pdfStyles.col4}>Receipt Date</Text>
              <Text style={pdfStyles.col5}>Payment Mode</Text>
              <Text style={pdfStyles.col6}>Amount</Text>
            </View>

            {/* Table Rows */}
            {filteredReceipts.map((receipt) => (
              <View key={receipt.id} style={pdfStyles.tableRow}>
                <Text style={pdfStyles.col1}>{receipt.receipt_number || '-'}</Text>
                <Text style={pdfStyles.col2}>{receipt.customer_name}</Text>
                <Text style={pdfStyles.col3}>{receipt.reference_no || '-'}</Text>
                <Text style={pdfStyles.col4}>{formatDate(receipt.receipt_date)}</Text>
                <Text style={pdfStyles.col5}>{receipt.payment_mode}</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(receipt.amount)}</Text>
              </View>
            ))}
          </View>

          <Text style={pdfStyles.footerText}>
            Showing {filteredReceipts.length} of {receipts.length} receipts
          </Text>
        </Page>
      </Document>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-lg text-gray-600">Loading receipts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">General Receipts</h1>
              <p className="text-xs sm:text-base text-white/80">Record and manage general receipt entries</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchReceipts}
                className="btn btn-sm sm:btn-md bg-white/20 text-white border-0 hover:bg-white/30 shrink-0"
              >
                🔄 Refresh
              </button>
              <button
                onClick={openReceiptModal}
                className="btn btn-sm sm:btn-md bg-white/20 text-white border-0 hover:bg-white/30 shrink-0"
              >
                ➕ New Receipt
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-base-100 shadow-xl p-4 sm:p-6 border-t-4 border-primary">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Search by Customer</span>
              </label>
              <input
                type="text"
                placeholder="Customer name..."
                className="input input-bordered focus:border-primary"
                value={searchCustomer}
                onChange={(e) => setSearchCustomer(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Search by Receipt No.</span>
              </label>
              <input
                type="text"
                placeholder="Receipt number..."
                className="input input-bordered focus:border-primary"
                value={searchReceiptNo}
                onChange={(e) => setSearchReceiptNo(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Date From</span>
              </label>
              <input
                type="date"
                className="input input-bordered focus:border-primary"
                value={searchDateFrom}
                onChange={(e) => setSearchDateFrom(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Date To</span>
              </label>
              <input
                type="date"
                className="input input-bordered focus:border-primary"
                value={searchDateTo}
                onChange={(e) => setSearchDateTo(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Payment Mode</span>
              </label>
              <select
                className="select select-bordered focus:border-primary"
                value={searchPaymentMode}
                onChange={(e) => setSearchPaymentMode(e.target.value)}
              >
                <option value="">All Modes</option>
                <option value="Cash">Cash</option>
                <option value="Bank">Bank</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {(searchCustomer || searchReceiptNo || searchDateFrom || searchDateTo || searchPaymentMode) && (
              <button
                onClick={() => {
                  setSearchCustomer('');
                  setSearchReceiptNo('');
                  setSearchDateFrom('');
                  setSearchDateTo('');
                  setSearchPaymentMode('');
                }}
                className="btn btn-sm btn-outline"
              >
                Clear Filters
              </button>
            )}
            <button
              onClick={exportToPDF}
              disabled={filteredReceipts.length === 0}
              className="btn btn-sm btn-primary"
              title="Export the current table as PDF"
            >
              📄 Export Table as PDF
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mt-4">
            <span>{error}</span>
            <button onClick={fetchReceipts} className="btn btn-sm">
              Retry
            </button>
          </div>
        )}

        {/* Receipts Table */}
        <div className="bg-base-100 shadow-xl rounded-b-2xl overflow-hidden">
          {filteredReceipts.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-lg text-gray-500">No receipts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                  <tr>
                    <th>Receipt No.</th>
                    <th>Customer</th>
                    <th>Reference #</th>
                    <th>Amount</th>
                    <th>Receipt Date</th>
                    <th>Payment Mode</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((receipt) => (
                    <tr key={receipt.id} className="hover:bg-base-200 transition-colors">
                      <td className="font-bold text-primary">{receipt.receipt_number || '-'}</td>
                      <td>
                        <div className="flex flex-col">
                          <span className="font-semibold">{receipt.customer_name || '-'}</span>
                        </div>
                      </td>
                      <td className="font-mono text-sm">{receipt.reference_no || '-'}</td>
                      <td>
                        <span className="font-mono font-bold">{formatCurrency(receipt.amount)}</span>
                      </td>
                      <td>{formatDate(receipt.receipt_date) || '-'}</td>
                      <td>
                        <div className="badge badge-primary">
                          {receipt.payment_mode}
                        </div>
                      </td>
                      <td className="text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => exportSingleReceiptPDF(receipt)}
                            className="btn btn-sm btn-info"
                            title="Export to PDF"
                          >
                            📄 PDF
                          </button>
                          <button
                            onClick={() => deleteReceipt(receipt.id)}
                            className="btn btn-sm btn-error"
                            title="Delete receipt"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mt-4 text-center text-sm text-gray-600">
          <p>Showing {filteredReceipts.length} of {receipts.length} receipts</p>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-6 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Add General Receipt</h2>
              <p className="text-gray-600 text-sm mt-1">
                Record a new receipt entry
              </p>
            </div>

            {/* Receipt Form */}
            <form onSubmit={submitReceipt} className="space-y-4">
              {/* Customer Name */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Customer Name *</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter customer name"
                  className={`input input-bordered focus:border-primary ${
                    receiptErrors.customer_name ? 'input-error' : ''
                  }`}
                  value={receiptForm.customer_name}
                  onChange={(e) => handleReceiptFormChange('customer_name', e.target.value)}
                  disabled={receiptSubmitting}
                />
                {receiptErrors.customer_name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.customer_name}
                    </span>
                  </label>
                )}
              </div>

              {/* Receipt Number */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Receipt Number</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., RCT-001 (optional)"
                  className="input input-bordered focus:border-primary"
                  value={receiptForm.receipt_number}
                  onChange={(e) => handleReceiptFormChange('receipt_number', e.target.value)}
                  disabled={receiptSubmitting}
                />
              </div>

              {/* Reference Number */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Reference # (Invoice/PO)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., INV-001 (optional)"
                  className="input input-bordered focus:border-primary"
                  value={receiptForm.reference_no}
                  onChange={(e) => handleReceiptFormChange('reference_no', e.target.value)}
                  disabled={receiptSubmitting}
                />
              </div>

              {/* Amount */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Amount *</span>
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={`input input-bordered focus:border-primary font-mono ${
                    receiptErrors.amount ? 'input-error' : ''
                  }`}
                  value={receiptForm.amount}
                  onChange={(e) => handleReceiptFormChange('amount', e.target.value)}
                  disabled={receiptSubmitting}
                />
                {receiptErrors.amount && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.amount}
                    </span>
                  </label>
                )}
              </div>

              {/* Receipt Date */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Receipt Date *</span>
                </label>
                <input
                  type="date"
                  className={`input input-bordered focus:border-primary ${
                    receiptErrors.receipt_date ? 'input-error' : ''
                  }`}
                  value={receiptForm.receipt_date}
                  onChange={(e) => handleReceiptFormChange('receipt_date', e.target.value)}
                  disabled={receiptSubmitting}
                />
                {receiptErrors.receipt_date && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.receipt_date}
                    </span>
                  </label>
                )}
              </div>

              {/* Payment Mode */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-semibold">Payment Mode *</span>
                </label>
                <select
                  className={`select select-bordered focus:border-primary ${
                    receiptErrors.payment_mode ? 'select-error' : ''
                  }`}
                  value={receiptForm.payment_mode}
                  onChange={(e) => handleReceiptFormChange('payment_mode', e.target.value)}
                  disabled={receiptSubmitting}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                  <option value="Cheque">Cheque</option>
                </select>
                {receiptErrors.payment_mode && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {receiptErrors.payment_mode}
                    </span>
                  </label>
                )}
              </div>

              {/* Submit Error */}
              {receiptErrors.submit && (
                <div className="alert alert-error">
                  <span>{receiptErrors.submit}</span>
                </div>
              )}

              {/* Success Message */}
              {receiptSuccess && (
                <div className="alert alert-success">
                  <span>{receiptSuccess}</span>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeReceiptModal}
                  className="btn btn-ghost flex-1"
                  disabled={receiptSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1 bg-gradient-to-r from-primary to-secondary border-0"
                  disabled={receiptSubmitting}
                >
                  {receiptSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Saving...
                    </>
                  ) : (
                    '💾 Add Receipt'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralReceipts;
